import { useRef, useContext, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import MapContext from "../../../MapContext";
import LayerPopup from "../LayerPopup";
import VectorSource from "ol/source/Vector.js";
import VectorLayer from "ol/layer/Vector";
import { getFeatureWithId } from "../../../utils/mapUtils";
import { setResultsForFeature, setResultsForLayer } from "../../../../redux/resultsSlice";
import { Style, Stroke, Circle as CircleStyle } from "ol/style";
import GroupPopup from "../GroupPopup";
import { Button } from "@progress/kendo-react-buttons";
import { parseFeatureDescription } from "./FeatureDescription";

const ResultsFeatures = () => {
    const dispatch = useDispatch();
    const [layer] = useSelector((state) => state.resultsData.resultsForLayer);

    const vectorLayer = useRef();
    const vectorHoverLayer = useRef();
    const results = useSelector((state) => state.resultsData.results);
    const { map } = useContext(MapContext);

    //Find layer for selected features from state
    const vectorLayerProperties = useSelector((state) =>
        state.configData.vectorLayerProperties.find((l) => l.Id === layer.Id)
    );

    // Create array of features from map, layer and feature Ids
    const featuresArray = [];
    layer.Features.forEach((featureId) => {
        let f = getFeatureWithId(map, layer.Id, featureId);
        if (f != null) {
            featuresArray.push(f);
        }
    });
    // Initiates rendering of <FeatureDescription/>
    // Undraws features
    const showFeatureDescription = (feature) => {
        dispatch(setResultsForFeature([{ ...layer, Features: [feature.id_] }]));
        vectorLayer.current.getSource().clear();
    };

    // Add/remove feature to hover layer when hovered over
    const handleMouseHover = (feature) => {
        vectorHoverLayer.current.getSource().addFeature(feature);
    };
    const handleMouseOut = (feature) => {
        vectorHoverLayer.current.getSource().removeFeature(feature);
    };
    /*
     Return function cleans up state and undraws feature styles
  
     Runs when back button is hit or another component is selected
    */
    useEffect(() => {
        vectorLayer.current = new VectorLayer({
            source: new VectorSource(),
            map: map,
            style: selectedStyle,
            zIndex: 100,
        });
        vectorHoverLayer.current = new VectorLayer({
            source: new VectorSource(),
            map: map,
            style: highlightStyle,
            zIndex: 100,
        });
        return () => {
            vectorLayer.current.getSource().clear();
            vectorHoverLayer.current.getSource().clear();
        };
    }, []);

    // Clears previous features and add new ones
    // Happens when feature selection changes & new features are all from one layer
    useEffect(() => {
        vectorLayer.current.getSource().addFeatures(featuresArray);
        vectorLayer.current.changed();
        return () => {
            vectorLayer.current.getSource().clear();
            vectorHoverLayer.current.getSource().clear();
        };
    }, [layer]);

    return (
        <div id="results-features">
            {results.length > 1 && (
                <Button
                    style={{
                        position: "absolute",
                        zIndex: "20",
                        cursor: "pointer",
                    }}
                    icon="arrow-left"
                    onClick={() => {
                        dispatch(setResultsForLayer([]));
                        dispatch(setResultsForFeature([]));
                    }}></Button>
            )}
            <div className="results-title">
                {layer.DisplayName} ({layer.Features.length})
            </div>
            <div className="results-options">
                <GroupPopup results={[layer]} icon={"menu"} />
            </div>
            <ul style={{ listStyleType: "none", padding: 0 }}>
                {featuresArray.map((feature) => {
                    return (
                        <div
                            key={feature.ol_uid}
                            className="results-feature"
                            onMouseOver={() => handleMouseHover(feature)}
                            onMouseOut={() => handleMouseOut(feature)}>
                            <ul style={{ listStyleType: "none", padding: 0 }}>
                                <li className="feature-row ">
                                    <div
                                        className="feature-label"
                                        onClick={() => showFeatureDescription(feature)}>
                                        {parseFeatureLabel(layer, feature.values_)}
                                    </div>
                                    <LayerPopup feature={feature} layerId={layer.Id} />
                                </li>
                                <li className="feature-description">
                                    {parseFeatureDescription(
                                        vectorLayerProperties,
                                        feature.values_
                                    )}
                                </li>
                            </ul>
                        </div>
                    );
                })}
            </ul>
        </div>
    );
};

export default ResultsFeatures;

// When multiple features from one layer are selected
// Example of input
/*
[{
    "DisplayName": "Parcel",
    "FeatureLabel": "{LayerDisplayName}",
    "FeatureDescription": "{PrimaryNameOne}<br/>{PropertyAddress}",
    "Id": "MbNYA6",
    "Features": [
        "1896",
        "3402",
        "3406",
        "3404"
    ]
}]

*/

const selectedStyle = new Style({
    zIndex: 100,
    stroke: new Stroke({
        color: "#FF0000",
        width: 3,
    }),
    image: new CircleStyle({
        radius: 10,
        stroke: new Stroke({
            color: "#FF0000",
            width: 4,
        }),
    }),
});

const highlightStyle = new Style({
    zIndex: 100,
    stroke: new Stroke({
        color: "#00FFFF",
        width: 3,
    }),
    image: new CircleStyle({
        radius: 10,
        stroke: new Stroke({
            color: "#00FFFF",
            width: 4,
        }),
    }),
});

const parseFeatureLabel = (layer, feature) => {
    let jsx = layer?.FeatureLabel;
    if (!jsx) {
        return <div>{layer.DisplayName}</div>;
    }
    if (feature == null) {
        return <div />;
    }
    jsx = jsx.replace("{LayerDisplayName}", layer.DisplayName);

    for (const property in feature) {
        if (typeof property === "string") {
            jsx = jsx.replaceAll(`{${property}}`, feature[property] ?? "");
        }
    }

    // In case of properties that were not in geojson
    //   but were in sld, replace with empty string
    jsx = jsx.replaceAll(/\{.*\}/g, "");

    return <div dangerouslySetInnerHTML={{ __html: jsx }} />;
};
/*
const parseFeatureDescription = (layer, feature) => {
    let jsx = layer?.FeatureDescription;
    if (!jsx || feature == null) {
        return <div />;
    }
    for (const property in feature) {
        if (typeof property === "string") {
            jsx = jsx.replaceAll(`{${property}}`, feature[property] ?? "");
        }
    }
    // In case of properties that were not in geojson
    //   but were in sld, replace with empty string
    jsx = jsx.replaceAll(/\{.*\}/g, "");

    return <div dangerouslySetInnerHTML={{ __html: jsx }} />;
};*/
